import { useEffect, useRef, useState, type RefObject } from "react";
import { CheckIcon, CopyIcon } from "@/components/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import {
  TASK_LOG_ROW_HEIGHT,
  formatLogs,
  formatLogTimestamp,
  type DisplayRow,
} from "../model/task-log-view";
import styles from "./TaskLogViewer.module.css";

const levelLabels = {
  Information: "Info",
  Warning: "Warn",
  Error: "Error",
};

function HighlightedMessage({ message, query }: { message: string; query: string }) {
  if (!query) return <>{message || " "}</>;

  const normalizedMessage = message.toLocaleLowerCase();
  const normalizedQuery = query.toLocaleLowerCase();
  const parts = [];
  let cursor = 0;
  let matchIndex = normalizedMessage.indexOf(normalizedQuery);

  while (matchIndex !== -1) {
    parts.push(message.slice(cursor, matchIndex));
    parts.push(
      <mark key={matchIndex}>{message.slice(matchIndex, matchIndex + query.length)}</mark>,
    );
    cursor = matchIndex + query.length;
    matchIndex = normalizedMessage.indexOf(normalizedQuery, cursor);
  }

  parts.push(message.slice(cursor));
  return <>{parts}</>;
}

type TaskLogViewportProps = {
  logCount: number;
  deferredQuery: string;
  displayRowCount: number;
  isError: boolean;
  isPending: boolean;
  isTaskActive: boolean;
  onScroll: () => void;
  startIndex: number;
  totalHeight: number;
  viewportRef: RefObject<HTMLDivElement | null>;
  visibleRows: DisplayRow[];
};

export function TaskLogViewport({
  logCount,
  deferredQuery,
  displayRowCount,
  isError,
  isPending,
  isTaskActive,
  onScroll,
  startIndex,
  totalHeight,
  viewportRef,
  visibleRows,
}: TaskLogViewportProps) {
  const copyFeedbackTimerRef = useRef<number | undefined>(undefined);
  const [copyFeedback, setCopyFeedback] = useState<{
    logId: string;
    message: "Copied" | "Copy failed";
  }>();

  useEffect(
    () => () => {
      if (copyFeedbackTimerRef.current) window.clearTimeout(copyFeedbackTimerRef.current);
    },
    [],
  );

  async function copyEntry(logId: string, message: string) {
    if (copyFeedbackTimerRef.current) window.clearTimeout(copyFeedbackTimerRef.current);

    try {
      await navigator.clipboard.writeText(message);
      setCopyFeedback({ logId, message: "Copied" });
    } catch {
      setCopyFeedback({ logId, message: "Copy failed" });
    }

    copyFeedbackTimerRef.current = window.setTimeout(() => setCopyFeedback(undefined), 1600);
  }

  return (
    <>
      <div className={styles.columnHeader} aria-hidden="true">
        <span>Time</span>
        <span>Level</span>
        <span>Message</span>
      </div>
      <div
        className={styles.viewport}
        ref={viewportRef}
        onScroll={onScroll}
        aria-label="Task log entries"
        tabIndex={0}
      >
        {displayRowCount === 0 ? (
          <div className={styles.emptyState}>
            <strong>
              {isPending
                ? "Loading output"
                : isError
                  ? "Unable to load logs"
                  : logCount === 0
                    ? "No output yet"
                    : "No logs match this view"}
            </strong>
            <span>
              {isError
                ? "Try refreshing the log."
                : logCount === 0 && isTaskActive
                  ? "Output will appear while the task runs."
                  : "Try another search or level filter."}
            </span>
          </div>
        ) : (
          <div className={styles.virtualCanvas} style={{ height: totalHeight }}>
            <div style={{ transform: `translateY(${startIndex * TASK_LOG_ROW_HEIGHT}px)` }}>
              {visibleRows.map((row) => (
                <div
                  className={styles.logRow}
                  data-level={row.log.level}
                  style={{ height: TASK_LOG_ROW_HEIGHT }}
                  key={row.key}
                >
                  <time
                    className={styles.timestamp}
                    dateTime={new Date(row.log.timestamp * 1000).toISOString()}
                    title={new Date(row.log.timestamp * 1000).toISOString()}
                  >
                    {row.lineIndex === 0 ? formatLogTimestamp(row.log.timestamp) : "↳"}
                  </time>
                  <span className={styles.level}>
                    {row.lineIndex === 0 ? levelLabels[row.log.level] : ""}
                  </span>
                  <code title={row.line}>
                    <HighlightedMessage message={row.line} query={deferredQuery} />
                  </code>
                  {row.lineIndex === 0 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className={styles.copyEntryButton}
                          type="button"
                          aria-label="Copy log entry"
                          data-copied={
                            copyFeedback?.logId === row.log.id &&
                            copyFeedback.message === "Copied"
                          }
                          onClick={() => void copyEntry(row.log.id, formatLogs([row.log]))}
                        >
                          {copyFeedback?.logId === row.log.id &&
                          copyFeedback.message === "Copied" ? (
                            <CheckIcon />
                          ) : (
                            <CopyIcon />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {copyFeedback?.logId === row.log.id
                          ? copyFeedback.message
                          : "Copy entry"}
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
